import type { NitroFetchOptions } from 'nitropack'
import { apiErrorHandler } from './helper/api-error-handler'
import { createOptions } from './helper/create-options'
import type { ValidateOptions } from './types/validate-options'

export function useApiFactory(
  fetch: typeof $fetch,
  options: { useToken: boolean }
) {
  const { useToken } = options

  const { formatUrl } = useUrl()

  function formatData(rawValue: any): any {
    if (rawValue instanceof FormData) {
      return rawValue
    }

    if (rawValue instanceof Date) {
      return rawValue.toISOString()
    }

    if (Array.isArray(rawValue)) {
      return rawValue.map((item) => {
        return formatData(item)
      })
    }

    if (rawValue && typeof rawValue === 'object') {
      return Object.entries(rawValue).reduce(
        (acc, [key, value]) => {
          acc[key] = formatData(value)
          return acc
        },
        {} as Record<string, any>
      )
    }

    return rawValue
  }

  return {
    get: async <TResponse>(
      url: string,
      options?: NitroFetchOptions<string>,
      validateOptions?: ValidateOptions<TResponse>
    ) => {
      const apiUrl = useEnvStore().apiBase
      const requestOptions = {
        ...options,
        query: options?.query ? formatData(options.query) : options?.query,
        ...createOptions({ ...validateOptions, apiUrl }),
        method: 'GET'
      } as const
      return fetch<TResponse>(formatUrl(url, useToken), requestOptions).catch(
        (error) => {
          return apiErrorHandler(error, {
            url,
            requestOptions
          })
        }
      )
    },

    post: async <TResponse>(
      url: string,
      data: any,
      options?: NitroFetchOptions<string>,
      validateOptions?: ValidateOptions<TResponse>
    ) => {
      const apiUrl = useEnvStore().apiBase
      const requestOptions = {
        ...options,
        ...createOptions({ ...validateOptions, apiUrl }),
        method: 'POST',
        body: formatData(data)
      } as const

      return fetch<TResponse>(formatUrl(url, useToken), requestOptions).catch(
        (error) =>
          apiErrorHandler(error, {
            url,
            requestOptions
          })
      )
    },

    patch: async <TResponse>(
      url: string,
      data: any,
      options?: NitroFetchOptions<string>,
      validateOptions?: ValidateOptions<TResponse>
    ) => {
      const apiUrl = useEnvStore().apiBase
      const requestOptions = {
        ...options,
        ...createOptions({ ...validateOptions, apiUrl }),
        method: 'PATCH',
        body: formatData(data)
      } as const

      return fetch<TResponse>(formatUrl(url, useToken), requestOptions).catch(
        (error) =>
          apiErrorHandler(error, {
            url,
            requestOptions
          })
      )
    },

    delete: async <TResponse>(
      url: string,
      options?: NitroFetchOptions<string>,
      validateOptions?: ValidateOptions<TResponse>
    ) => {
      const apiUrl = useEnvStore().apiBase
      const requestOptions = {
        ...options,
        ...createOptions({ ...validateOptions, apiUrl }),
        method: 'DELETE'
      } as const
      return fetch<TResponse>(formatUrl(url, useToken), requestOptions).catch(
        (error) =>
          apiErrorHandler(error, {
            url,
            requestOptions
          })
      )
    }
  }
}
